import { idsIncluded, joinIds, updateWithToast } from '@/clientOnlyUtils/clientUtils';

import { OrgDetailPageCore } from '@poseidon/components/OrgDetail';
import type { OrgDetailData, OrgDetailMutations } from '@poseidon/components/OrgDetail';
import {
  useMakeOrgFieldPrimaryMutation,
  useOrgSetFlagsMutation,
  useOrgUpdateMutation,
  useRemoveOrgFieldPrimaryMutation,
  useRemoveOrgFieldValueMutation,
} from '@/hooks/trpc/useOrgMutation';
import { useContactSetStageMutation } from '~/hooks/trpc/useContactMutation';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { api } from '@/utils/api';
import { openContactDetailQuery } from '~/components/ContactCard/ContactCard';
import { convertStringToOrgID } from '~/components/OrgSwitcher';

type OrgDetailPage = {
  orgId?: string;
  authTeamId: string;
  orgFirst?: boolean;
  onClose: () => void;
};

export const OrgDetailPage = ({ orgId, authTeamId, orgFirst, onClose }: OrgDetailPage) => {
  const [query, setQuery] = useRouterQuery();
  const activeTab = convertStringToOrgID(query.detailType, 'interact');
  const detailQuery = api.org.detailPage.useQuery({ orgId: orgId || '' }, { enabled: Boolean(orgId) });

  const orgUpdate = useOrgUpdateMutation();
  const removeFieldValue = useRemoveOrgFieldValueMutation();
  const makeFieldPrimary = useMakeOrgFieldPrimaryMutation();
  const removeFieldPrimary = useRemoveOrgFieldPrimaryMutation();
  const setFlags = useOrgSetFlagsMutation();
  const setContactStage = useContactSetStageMutation();

  // `org.detailPage` already returns exactly the OrgDetailData shape, so pass it
  // through without reshaping (a typed const widens the wire superset).
  const data: OrgDetailData | null = detailQuery.data ?? null;

  const mutations: OrgDetailMutations = {
    updateNotes: (notes) =>
      void updateWithToast({
        loading: 'Saving notes...',
        success: 'Notes saved',
        mutationFnAsync: () => orgUpdate.mutateAsync({ teamId: authTeamId, orgId: orgId || '', data: { source: 'crm', notes } }),
      }),
    onSetFlag: (flag, value) =>
      void updateWithToast({
        loading: 'Updating...',
        success: 'Updated',
        mutationFnAsync: () =>
          setFlags.mutateAsync({
            orgId: orgId || '',
            ...(flag === 'isRsuGranting' ? { isRsuGranting: value } : { isEquityGranting: value }),
          }),
      }),
    onSetHqState: (value) =>
      void updateWithToast({
        loading: 'Updating HQ state...',
        success: 'Updated',
        mutationFnAsync: () => setFlags.mutateAsync({ orgId: orgId || '', hqState: value }),
      }),
    onAddValue: (name, value, source) =>
      void updateWithToast({
        loading: 'Adding...',
        success: 'Added',
        mutationFnAsync: () =>
          orgUpdate.mutateAsync({ orgId: orgId || '', teamId: authTeamId, data: { [name]: value, source, primary: true } }),
      }),
    onDeleteValue: (field, source) =>
      void updateWithToast({
        loading: 'Deleting...',
        success: 'Deleted',
        mutationFnAsync: () => removeFieldValue.mutateAsync({ orgId: orgId || '', teamId: authTeamId, field, source }),
      }),
    onMakePrimary: (field, source) =>
      void updateWithToast({
        loading: 'Updating...',
        success: 'Marked as primary',
        mutationFnAsync: () => makeFieldPrimary.mutateAsync({ orgId: orgId || '', data: { field, source } }),
      }),
    onRemovePrimary: (field, source) =>
      void updateWithToast({
        loading: 'Updating...',
        success: 'Primary removed',
        mutationFnAsync: () => removeFieldPrimary.mutateAsync({ orgId: orgId || '', teamId: authTeamId, data: { field, source } }),
      }),
    setContactStage: (contactId, stageId) => setContactStage.mutate({ contactId, stageId }),
    isFlagsPending: setFlags.isPending,
    stageUpdatingContactId: setContactStage.isPending ? (setContactStage.variables?.contactId ?? null) : null,
  };

  return (
    <OrgDetailPageCore
      data={data}
      isLoading={detailQuery.isPending}
      mutations={mutations}
      activeTab={activeTab}
      onTabChange={(detailType) => setQuery((o) => ({ ...o, detailType }))}
      onClose={onClose}
      onContactClick={({ contactId, cadenceId }) =>
        setQuery((q) => openContactDetailQuery(q, { contactId, cadenceId, dontClearOrg: orgFirst }))
      }
      isContactActive={(contactId, cadenceId) => query.id === joinIds({ contactId, cadenceId })}
      isContactChecked={(contactId, cadenceId) => idsIncluded({ contactId, cadenceId }, query.selectedIds)}
    />
  );
};
